import type { AccountType, AdminRole } from "@/lib/admin-access";

export type PlanAudience = "player" | "venue";
export type BillingInterval = "free" | "monthly" | "annual";

export type PlanKey =
  | "player_free"
  | "player_plus_beta_monthly"
  | "player_plus_monthly"
  | "venue_setup"
  | "venue_start_monthly"
  | "venue_founders_monthly"
  | "venue_pro_monthly"
  | "venue_pro_annual";

export type PlanEntitlements = Record<string, boolean | number | string | null | undefined>;
type PlanCatalogEntry = {
  audience: PlanAudience;
  name: string;
  description: string | null;
  billing_interval: BillingInterval;
  amount_cents: number;
  currency: string;
  recommended: boolean;
  entitlements: PlanEntitlements;
  metadata: Record<string, unknown>;
};

export interface PlanSnapshot {
  key: PlanKey | string;
  audience: PlanAudience;
  name: string;
  description: string | null;
  billing_interval: BillingInterval;
  amount_cents: number;
  currency: string;
  recommended: boolean;
  entitlements: PlanEntitlements;
  metadata: Record<string, unknown>;
}

export interface EffectivePlanSnapshot {
  subscription_id: string | null;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  provider: string | null;
  provider_customer_id: string | null;
  provider_subscription_id: string | null;
  plan: PlanSnapshot;
}

export interface TenantAccessSnapshot {
  tenant_id: string;
  role: string;
  status: string;
  tenant_name: string;
  tenant_slug: string;
  tenant_status: string;
  plan: EffectivePlanSnapshot;
}

export interface AccessSnapshot {
  user_id: string;
  account_type: AccountType;
  roles: string[];
  admin_role: AdminRole | null;
  player_plan: EffectivePlanSnapshot;
  tenant_memberships: TenantAccessSnapshot[];
  permissions: {
    can_access_player_app: boolean;
    can_access_venue_workspace: boolean;
    can_access_admin: boolean;
    can_manage_platform_plans: boolean;
    can_manage_platform_settings: boolean;
  };
}

export type VenueWorkspaceMode = "partner" | "venue_pro";

export function isPlanEntitlementEnabled(status: string | null | undefined) {
  return ["active", "trialing", "past_due", "incomplete"].includes(status ?? "");
}

export const publicPlanCatalog = {
  player_free: {
    audience: "player",
    name: "Match Free",
    description: "Para conhecer a Match, encontrar quadras e reservar com praticidade.",
    currency: "BRL",
    recommended: false,
    amount_cents: 0,
    billing_interval: "free",
    entitlements: {
      can_discover: true,
      can_book: true,
      can_view_bookings: true,
      plus: false,
      max_active_bookings: 3,
      active_bookings_unlimited: false,
      can_use_match_chat: false,
      can_access_plus_arena: false,
      can_create_plus_groups: false,
      can_join_plus_groups: false,
      can_view_arena_ranking: false,
      favorites_limited: true,
      favorites_unlimited: false,
      favorites_limit: 5,
      can_repeat_booking: false,
      can_use_favorite_slot_alerts: false,
      partner_discount_percent: 0,
      can_access_marketplace_offers: false,
      can_use_progressive_benefits: false,
      can_access_early_campaigns: false,
      can_receive_offers: false,
      support_level: "standard",
    },
    metadata: {
      card_label: "Comece gratis",
      commercial_summary: "Entre, descubra e comece a usar.",
      included_now: [
        "Ate 3 agendamentos ativos",
        "Reserva com disponibilidade real",
        "Acesso basico ao ecossistema Match",
      ],
      coming_soon_features: [],
      cta_label: "Comecar gratis",
    },
  },
  player_plus_beta_monthly: {
    audience: "player",
    name: "Match Plus Beta",
    description: "Entrada mais leve para quem joga com frequencia e quer reduzir atrito na rotina esportiva.",
    currency: "BRL",
    recommended: true,
    amount_cents: 990,
    billing_interval: "monthly",
    entitlements: {
      can_discover: true,
      can_book: true,
      can_view_bookings: true,
      plus: true,
      max_active_bookings: null,
      active_bookings_unlimited: true,
      can_use_match_chat: true,
      can_access_plus_arena: true,
      can_create_plus_groups: true,
      can_join_plus_groups: true,
      can_view_arena_ranking: true,
      favorites_limited: false,
      favorites_unlimited: true,
      can_repeat_booking: true,
      can_use_favorite_slot_alerts: true,
      partner_discount_percent: 10,
      can_access_marketplace_offers: false,
      can_use_progressive_benefits: false,
      can_access_early_campaigns: true,
      can_receive_offers: true,
      priority_benefits: false,
      support_level: "priority",
    },
    metadata: {
      card_label: "Entrada mais facil",
      commercial_summary: "Entre no Plus com valor beta e resolva sua rotina de jogo com menos friccao.",
      included_now: [
        "Agendamentos ativos ilimitados",
        "Favoritos ilimitados",
        "Repetir reserva em poucos toques",
        "Alerta quando abrir vaga no horario favorito",
        "Alerta de oferta nas quadras favoritas",
        "10% em parceiros participantes",
        "Acesso antecipado a campanhas e novidades",
        "Suporte prioritario",
        "Arena Match Plus com grupos, duelos e ranking",
        "Chat contextual por duelo e partida",
      ],
      coming_soon_features: [
        "Reserva prioritaria em horarios disputados",
        "Cashback e credito Match",
        "Convite rapido para amigos e grupo",
        "Historico inteligente de quadras e horarios",
        "Beneficios progressivos por uso",
        "Ofertas exclusivas do marketplace",
      ],
      cta_label: "Assinar Plus Beta",
      publicly_listed: true,
    },
  },
  player_plus_monthly: {
    audience: "player",
    name: "Match Plus Legacy",
    description: "Plano legado mantido para assinantes anteriores ao reajuste comercial.",
    currency: "BRL",
    recommended: false,
    amount_cents: 2190,
    billing_interval: "monthly",
    entitlements: {
      can_discover: true,
      can_book: true,
      can_view_bookings: true,
      plus: true,
      max_active_bookings: null,
      active_bookings_unlimited: true,
      can_use_match_chat: true,
      can_access_plus_arena: true,
      can_create_plus_groups: true,
      can_join_plus_groups: true,
      can_view_arena_ranking: true,
      favorites_limited: false,
      favorites_unlimited: true,
      can_repeat_booking: true,
      can_use_favorite_slot_alerts: true,
      partner_discount_percent: 10,
      can_access_marketplace_offers: false,
      can_use_progressive_benefits: false,
      can_access_early_campaigns: true,
      can_receive_offers: true,
      priority_benefits: false,
      support_level: "priority",
    },
    metadata: {
      card_label: "Plano legado",
      commercial_summary: "Assinatura mantida para clientes anteriores, sem nova venda publica.",
      included_now: [
        "Agendamentos ativos ilimitados",
        "Favoritos ilimitados",
        "Repetir reserva em poucos toques",
        "Alerta quando abrir vaga no horario favorito",
        "Alerta de oferta nas quadras favoritas",
        "10% em parceiros participantes",
        "Acesso antecipado a campanhas e novidades",
        "Suporte prioritario",
        "Arena Match Plus com grupos, duelos e ranking",
        "Chat contextual por duelo e partida",
      ],
      coming_soon_features: [
        "Reserva prioritaria em horarios disputados",
        "Cashback e credito Match",
        "Convite rapido para amigos e grupo",
        "Historico inteligente de quadras e horarios",
        "Beneficios progressivos por uso",
        "Ofertas exclusivas do marketplace",
      ],
      cta_label: "Gerenciar Match Plus",
      publicly_listed: false,
      internal: true,
      legacy_plan: true,
    },
  },
  venue_setup: {
    audience: "venue",
    name: "Estabelecimento Setup",
    description: "Etapa inicial para configurar perfil e preparar a operacao antes da publicacao.",
    currency: "BRL",
    recommended: false,
    amount_cents: 0,
    billing_interval: "free",
    entitlements: {
      can_setup_profile: true,
      can_manage_partner_profile: true,
      can_publish_partner_page: true,
      can_manage_partner_offers: false,
      can_view_partner_metrics: true,
      can_publish_venue: false,
      can_access_venue_analytics: false,
      can_receive_bookings: false,
      can_use_store: false,
      can_use_store_pix: false,
      can_use_finance_dashboard: false,
      max_courts: 0,
    },
    metadata: {
      card_label: "Preparacao inicial",
      commercial_summary: "Configure o perfil antes de abrir a operacao.",
      included_now: ["Perfil inicial", "Base da operacao", "Preparacao para publicacao"],
      coming_soon_features: [],
      cta_label: "Concluir setup",
    },
  },
  venue_start_monthly: {
    audience: "venue",
    name: "Match Start",
    description: "Entrada inteligente para publicar a arena, vender online e organizar a rotina essencial com foco em tracao inicial.",
    currency: "BRL",
    recommended: false,
    amount_cents: 9900,
    billing_interval: "monthly",
    entitlements: {
      can_setup_profile: true,
      can_manage_partner_profile: true,
      can_publish_partner_page: true,
      can_manage_partner_offers: true,
      can_view_partner_metrics: true,
      can_publish_venue: true,
      can_manage_courts: true,
      can_receive_bookings: true,
      can_receive_online_payments: true,
      can_access_venue_analytics: false,
      can_use_store: false,
      can_use_store_pix: false,
      can_use_finance_dashboard: false,
      can_manage_staff: false,
      max_courts: 2,
    },
    metadata: {
      card_label: "Entrada comercial",
      commercial_summary: "Publicacao, agenda e venda online para destravar os primeiros parceiros com operacao objetiva e clara.",
      included_now: [
        "Pagina publica da arena",
        "Agenda online e reservas",
        "Pagamentos basicos",
        "Painel operacional direto ao ponto",
        "Ate 2 quadras",
      ],
      coming_soon_features: [],
      cta_label: "Comecar no Start",
      publicly_listed: true,
    },
  },
  venue_founders_monthly: {
    audience: "venue",
    name: "Oferta Fundadores",
    description: "Oferta limitada com valor de entrada e pacote Pro durante a fase inicial de tracao.",
    currency: "BRL",
    recommended: false,
    amount_cents: 9900,
    billing_interval: "monthly",
    entitlements: {
      can_setup_profile: true,
      can_manage_partner_profile: true,
      can_publish_partner_page: true,
      can_manage_partner_offers: true,
      can_view_partner_metrics: true,
      can_publish_venue: true,
      can_manage_courts: true,
      can_receive_bookings: true,
      can_receive_online_payments: true,
      can_access_venue_analytics: true,
      can_use_store: true,
      can_use_store_pix: true,
      can_use_finance_dashboard: true,
      can_manage_staff: true,
      max_courts: null,
    },
    metadata: {
      card_label: "Oferta limitada",
      commercial_summary: "Preco de entrada com operacao Pro por 6 meses para acelerar os primeiros cases.",
      included_now: [
        "Agenda, reservas e venda online",
        "Analytics executivo",
        "Financeiro e loja/caixa",
        "Suporte prioritario",
      ],
      coming_soon_features: [],
      cta_label: "Ativar oferta Fundadores",
      publicly_listed: false,
      promo_duration_months: 6,
      promo_target_plan_key: "venue_pro_monthly",
      founders_offer: true,
    },
  },
  venue_pro_monthly: {
    audience: "venue",
    name: "Match Pro",
    description: "Plano principal para operar agenda, venda online, analytics, financeiro e operacao ampliada.",
    currency: "BRL",
    recommended: true,
    amount_cents: 14900,
    billing_interval: "monthly",
    entitlements: {
      can_setup_profile: true,
      can_manage_partner_profile: true,
      can_publish_partner_page: true,
      can_manage_partner_offers: true,
      can_view_partner_metrics: true,
      can_publish_venue: true,
      can_manage_courts: true,
      can_receive_bookings: true,
      can_receive_online_payments: true,
      can_access_venue_analytics: true,
      can_use_store: true,
      can_use_store_pix: true,
      can_use_finance_dashboard: true,
      can_manage_staff: true,
      max_courts: null,
    },
    metadata: {
      card_label: "Plano principal",
      commercial_summary: "Operacao, venda online, analytics e caixa no pacote que deve vender melhor no inicio.",
      included_now: [
        "Quadras e agenda sem limite pratico",
        "Analytics executivo",
        "Financeiro e equipe",
        "Loja, caixa e vendas",
      ],
      coming_soon_features: [],
      cta_label: "Assinar Match Pro",
      publicly_listed: true,
    },
  },
  venue_pro_annual: {
    audience: "venue",
    name: "Match Pro Anual",
    description: "Plano anual para previsibilidade, caixa melhor e operacao completa o ano inteiro.",
    currency: "BRL",
    recommended: true,
    amount_cents: 149900,
    billing_interval: "annual",
    entitlements: {
      can_setup_profile: true,
      can_manage_partner_profile: true,
      can_publish_partner_page: true,
      can_manage_partner_offers: true,
      can_view_partner_metrics: true,
      can_publish_venue: true,
      can_manage_courts: true,
      can_receive_bookings: true,
      can_receive_online_payments: true,
      can_access_venue_analytics: true,
      can_use_store: true,
      can_use_store_pix: true,
      can_use_finance_dashboard: true,
      can_manage_staff: true,
      annual_recommended: true,
      max_courts: null,
    },
    metadata: {
      card_label: "Melhor caixa",
      commercial_summary: "Economia anual com o pacote Pro completo e menos churn na base parceira.",
      included_now: [
        "Economia frente ao mensal",
        "Agenda, reservas e operacao ampliada",
        "Base ideal para operar o ano todo",
      ],
      coming_soon_features: [],
      cta_label: "Assinar anual",
      publicly_listed: true,
    },
  },
} as const satisfies Record<PlanKey, PlanCatalogEntry>;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function asString(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

function normalizePlan(value: unknown, fallbackKey: PlanKey): PlanSnapshot {
  const source = asRecord(value);
  const fallback = publicPlanCatalog[fallbackKey] ?? publicPlanCatalog.player_free;
  const entitlements = {
    ...fallback.entitlements,
    ...asRecord(source.entitlements),
  } as PlanEntitlements;
  const metadata = {
    ...fallback.metadata,
    ...asRecord(source.metadata),
  };

  return {
    key: asString(source.key, fallbackKey),
    audience:
      source.audience === "player" || source.audience === "venue"
        ? source.audience
        : fallback.audience,
    name: asString(source.name, fallback.name),
    description: typeof source.description === "string" ? source.description : fallback.description,
    billing_interval:
      source.billing_interval === "annual" || source.billing_interval === "monthly"
        ? source.billing_interval
        : fallback.billing_interval,
    amount_cents: asNumber(source.amount_cents, fallback.amount_cents),
    currency: asString(source.currency, fallback.currency),
    recommended: typeof source.recommended === "boolean" ? source.recommended : fallback.recommended,
    entitlements,
    metadata,
  };
}

function normalizeEffectivePlan(value: unknown, fallbackKey: PlanKey): EffectivePlanSnapshot {
  const source = asRecord(value);
  return {
    subscription_id: typeof source.subscription_id === "string" ? source.subscription_id : null,
    status: asString(source.status, "active"),
    current_period_start: typeof source.current_period_start === "string" ? source.current_period_start : null,
    current_period_end: typeof source.current_period_end === "string" ? source.current_period_end : null,
    cancel_at_period_end: asBoolean(source.cancel_at_period_end, false),
    provider: typeof source.provider === "string" ? source.provider : null,
    provider_customer_id: typeof source.provider_customer_id === "string" ? source.provider_customer_id : null,
    provider_subscription_id: typeof source.provider_subscription_id === "string" ? source.provider_subscription_id : null,
    plan: normalizePlan(source.plan, fallbackKey),
  };
}

function normalizeTenantMembership(value: unknown): TenantAccessSnapshot | null {
  const source = asRecord(value);
  const tenantId = asString(source.tenant_id);
  if (!tenantId) return null;

  return {
    tenant_id: tenantId,
    role: asString(source.role, "staff"),
    status: asString(source.status, "active"),
    tenant_name: asString(source.tenant_name, "Estabelecimento"),
    tenant_slug: asString(source.tenant_slug),
    tenant_status: asString(source.tenant_status, "active"),
    plan: normalizeEffectivePlan(source.plan, "venue_setup"),
  };
}

export function normalizeAccessSnapshot(value: unknown): AccessSnapshot | null {
  const source = asRecord(value);
  const userId = asString(source.user_id);
  if (!userId) return null;

  const roles = Array.isArray(source.roles)
    ? source.roles.filter((role): role is string => typeof role === "string")
    : [];
  const tenantMemberships = Array.isArray(source.tenant_memberships)
    ? source.tenant_memberships.map(normalizeTenantMembership).filter((item): item is TenantAccessSnapshot => Boolean(item))
    : [];
  const permissions = asRecord(source.permissions);
  const accountType = source.account_type === "admin" || source.account_type === "venue" ? source.account_type : "player";
  const adminRole = typeof source.admin_role === "string" ? source.admin_role as AdminRole : null;

  return {
    user_id: userId,
    account_type: accountType,
    roles,
    admin_role: adminRole,
    player_plan: normalizeEffectivePlan(source.player_plan, "player_free"),
    tenant_memberships: tenantMemberships,
    permissions: {
      can_access_player_app: asBoolean(permissions.can_access_player_app, true),
      can_access_venue_workspace: asBoolean(permissions.can_access_venue_workspace),
      can_access_admin: asBoolean(permissions.can_access_admin),
      can_manage_platform_plans: asBoolean(permissions.can_manage_platform_plans),
      can_manage_platform_settings: asBoolean(permissions.can_manage_platform_settings),
    },
  };
}

export function hasPlanFeature(plan: EffectivePlanSnapshot | PlanSnapshot | null | undefined, feature: string) {
  if (!plan) return false;
  if ("plan" in plan && !isPlanEntitlementEnabled(plan.status)) {
    return false;
  }

  const planNode = "plan" in plan ? plan.plan : plan;
  return planNode?.entitlements?.[feature] === true;
}

export function hasVenuePlanFeature(
  snapshot: AccessSnapshot | null | undefined,
  tenantId: string,
  feature: string,
) {
  const membership = snapshot?.tenant_memberships.find((item) => item.tenant_id === tenantId);
  return hasPlanFeature(membership?.plan, feature);
}

export function getTenantIdsWithPlanFeature(
  snapshot: AccessSnapshot | null | undefined,
  tenantIds: string[],
  feature: string,
) {
  return tenantIds.filter((tenantId) => hasVenuePlanFeature(snapshot, tenantId, feature));
}

export function hasAnyTenantPlanFeature(
  snapshot: AccessSnapshot | null | undefined,
  feature: string,
) {
  return (snapshot?.tenant_memberships ?? []).some((membership) =>
    hasPlanFeature(membership.plan, feature),
  );
}

export function getVenueWorkspaceMode(plan: EffectivePlanSnapshot | PlanSnapshot | null | undefined): VenueWorkspaceMode {
  if (
    hasPlanFeature(plan, "can_receive_bookings") ||
    hasPlanFeature(plan, "can_manage_courts") ||
    hasPlanFeature(plan, "can_use_store") ||
    hasPlanFeature(plan, "can_use_finance_dashboard")
  ) {
    return "venue_pro";
  }

  return "partner";
}

export function getDefaultVenueWorkspacePath(
  snapshot: AccessSnapshot | null | undefined,
  tenantRoles: Array<{ tenant_id: string; role: string }>,
) {
  if (tenantRoles.length === 0) {
    return "/app/venue/setup";
  }

  const memberships = snapshot?.tenant_memberships ?? [];
  if (memberships.some((membership) => getVenueWorkspaceMode(membership.plan) === "venue_pro")) {
    return "/app/venue/dashboard";
  }

  return "/app/partner";
}
